// Password strength utilities (UI-only — validation logic lives in @vedjegy/shared)
export { validatePassword } from '@vedjegy/shared';

export interface PasswordRule {
  label: string;
  met: boolean;
}

export interface PasswordStrength {
  score: number;   // 0 = empty, 1 = weak, 2 = fair, 3 = strong, 4 = very strong
  label: string;
  color: string;
  rules: PasswordRule[];
}

const RULES_EMPTY: PasswordRule[] = [
  { label: 'Legalább 8 karakter',        met: false },
  { label: 'Kisbetű (a–z)',              met: false },
  { label: 'Nagybetű (A–Z)',             met: false },
  { label: 'Szám (0–9)',                 met: false },
  { label: 'Speciális karakter (!@#…)',  met: false },
];

export function getPasswordStrength(pw: string): PasswordStrength {
  if (!pw) return { score: 0, label: '', color: '#e5e7eb', rules: RULES_EMPTY };

  const rules: PasswordRule[] = [
    { label: 'Legalább 8 karakter',       met: pw.length >= 8 },
    { label: 'Kisbetű (a–z)',             met: /[a-z]/.test(pw) },
    { label: 'Nagybetű (A–Z)',            met: /[A-Z]/.test(pw) },
    { label: 'Szám (0–9)',                met: /[0-9]/.test(pw) },
    { label: 'Speciális karakter (!@#…)', met: /[^a-zA-Z0-9]/.test(pw) },
  ];

  const met = rules.filter((r) => r.met).length;

  if (met <= 2) return { score: 1, label: 'Gyenge',        color: '#ef4444', rules };
  if (met === 3) return { score: 2, label: 'Közepes',      color: '#f59e0b', rules };
  if (met === 4) return { score: 3, label: 'Erős',         color: '#3b82f6', rules };
  return           { score: 4, label: 'Nagyon erős', color: '#22c55e', rules };
}

/** Inline password strength bar + rule checklist. */
export function PasswordStrengthBar({ password }: { password: string }) {
  const s = getPasswordStrength(password);
  if (!password) return null;

  return (
    <div style={{ marginTop: 8, marginBottom: 4 }}>
      {/* Segmented bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
        {[1, 2, 3, 4].map((seg) => (
          <div
            key={seg}
            style={{
              flex: 1, height: 4, borderRadius: 99,
              background: s.score >= seg ? s.color : 'var(--border)',
              transition: 'background .2s',
            }}
          />
        ))}
        {s.label && (
          <span style={{ fontSize: 11, fontWeight: 700, color: s.color, whiteSpace: 'nowrap', marginLeft: 6, minWidth: 84 }}>
            {s.label}
          </span>
        )}
      </div>
      {/* Rule checklist */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 14px' }}>
        {s.rules.map((r, i) => (
          <span
            key={i}
            style={{
              fontSize: 11,
              color: r.met ? '#166534' : 'var(--muted-foreground)',
              display: 'flex', alignItems: 'center', gap: 3,
            }}
          >
            <span style={{ fontSize: 10, lineHeight: 1 }}>{r.met ? '✓' : '○'}</span>
            {r.label}
          </span>
        ))}
      </div>
    </div>
  );
}
